import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authApi } from '../api/auth';
import { cardsApi } from '../api/cards';
import { usersApi } from '../api/users';

const OnboardingGuard = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);
  const lastPathRef = useRef(location.pathname);
  const isCheckingRef = useRef(false);
  const hasCheckedInitialRef = useRef(false);

  useEffect(() => {
    // Prevent multiple simultaneous checks
    if (isCheckingRef.current) return;

    // Always run initial check once, then only on path changes
    if (hasCheckedInitialRef.current && lastPathRef.current === location.pathname) {
      return;
    }

    lastPathRef.current = location.pathname;
    hasCheckedInitialRef.current = true;
    isCheckingRef.current = true;
    setIsChecking(true);
    
    const checkOnboarding = async () => {
      const currentPath = location.pathname;
      const finishCheck = () => {
        isCheckingRef.current = false;
        setIsChecking(false);
      };

      // Skip onboarding checks on auth pages
      if (currentPath === '/login' || currentPath === '/register' || currentPath === '/forgot-password') {
        finishCheck();
        return;
      }

      const user = authApi.getCurrentUser();
      
      // If no user, allow access to auth pages
      if (!user) {
        // Allow access to register, login, and onboarding pages
        if (currentPath.startsWith('/onboarding') || 
            currentPath === '/register' || 
            currentPath === '/login' ||
            currentPath === '/') {
          finishCheck();
          return;
        }
        // Redirect to login if trying to access protected pages
        if (currentPath !== '/login' && currentPath !== '/register') {
          navigate('/login', { replace: true });
        }
        finishCheck();
        return;
      }

      // Admin users should never go through onboarding
      if (user.isAdmin) {
        finishCheck();
        return;
      }

      let resolvedUser = user;
      if (user.id) {
        try {
          const userResponse = await usersApi.getById(user.id);
          if (userResponse?.user) {
            resolvedUser = { ...user, ...userResponse.user };
            localStorage.setItem('user', JSON.stringify(resolvedUser));
          }
        } catch {
          // Fall back to cached local user if profile refresh fails.
        }
      }

      // Check if user has selected language
      const hasLanguage = resolvedUser.language || localStorage.getItem('language');
      
      // Check if user has completed tutorial
      // Handle both boolean and numeric values (0/1 from database)
      const tutorialCompleted = resolvedUser.tutorial_completed === true || 
                                 resolvedUser.tutorial_completed === 1 || 
                                 resolvedUser.tutorial_completed === '1';
      const starterPackReceivedCount = Number(
        resolvedUser.starter_pack_received_count ??
        resolvedUser.starterPackReceivedCount ??
        0
      );
      const hasReceivedStarterPack = resolvedUser.has_received_starter_pack === true ||
                                     resolvedUser.has_received_starter_pack === 1 ||
                                     resolvedUser.has_received_starter_pack === '1' ||
                                     starterPackReceivedCount >= 30;
      
      // If language not selected, redirect to language selection (unless already there or in pack opening)
      if (!hasLanguage) {
        if (currentPath !== '/onboarding/language' && 
            currentPath !== '/onboarding/pack-opening') {
          navigate('/onboarding/language', { replace: true });
          finishCheck();
          return;
        }
      }
      
      try {
        const cardsResponse = await cardsApi.getUserCards(resolvedUser.id, {});
        const hasCards = cardsResponse?.cards && cardsResponse.cards.length > 0;
        const hasOnboardingCards = hasReceivedStarterPack || hasCards;

        // If language selected but tutorial not completed, allow skip when cards already exist
        if (hasLanguage && !tutorialCompleted) {
          if (hasOnboardingCards) {
            const updatedUser = { ...resolvedUser, tutorial_completed: true };
            localStorage.setItem('user', JSON.stringify(updatedUser));
            usersApi.updateTutorialCompleted(resolvedUser.id, true).catch(() => {});
            if (currentPath.startsWith('/onboarding')) {
              navigate('/', { replace: true });
            }
            finishCheck();
            return;
          }

          if (currentPath === '/onboarding/language') {
            // Allow staying on language page if just selected
            finishCheck();
            return;
          }
          if (!currentPath.startsWith('/onboarding')) {
            navigate('/onboarding/tutorial', { replace: true });
            finishCheck();
            return;
          }
        } else if (tutorialCompleted) {
          // If tutorial completed, check if user has already opened starter pack
          if (hasOnboardingCards) {
            // If user has cards, onboarding is complete - don't allow access to onboarding pages
            if (currentPath === '/onboarding/tutorial' || 
                currentPath === '/onboarding/language' ||
                currentPath === '/onboarding/pack-opening') {
              navigate('/', { replace: true });
              finishCheck();
              return;
            }
          } else {
            // Tutorial completed but no cards yet - redirect to pack opening
            if (currentPath !== '/onboarding/pack-opening') {
              if (currentPath === '/onboarding/tutorial' || 
                  currentPath === '/onboarding/language') {
                navigate('/onboarding/pack-opening', { replace: true });
                finishCheck();
                return;
              }
              // If trying to access other pages, redirect to pack opening
              navigate('/onboarding/pack-opening', { replace: true });
              finishCheck();
              return;
            }
          }
        }
      } catch (error) {
        console.error('Error checking user cards:', error);
        // On error, allow access but don't block (fail gracefully)
        // If the starter pack is already assigned, never trap users in onboarding.
        if (hasReceivedStarterPack && currentPath.startsWith('/onboarding')) {
          navigate('/', { replace: true });
          finishCheck();
          return;
        }
        // If tutorial completed, don't allow access to tutorial or language pages
        if (tutorialCompleted && (currentPath === '/onboarding/tutorial' || 
            currentPath === '/onboarding/language')) {
          navigate('/', { replace: true });
          finishCheck();
          return;
        }
      }

      finishCheck();
    };

    checkOnboarding();
  }, [location.pathname, navigate]);

  // Prevent onboarding pages from flashing during checks
  if (isChecking && location.pathname.startsWith('/onboarding')) {
    return null;
  }

  // Show children immediately to prevent flash, but guard will handle redirects
  return <>{children}</>;
};

export default OnboardingGuard;

